import { isApiError, isTooManyRequests, type ApiError } from "@/lib/api/client";
import type { TranslationStrings } from "@/lib/i18n/translations";

/** Per-field messages keyed by input name / API detail key. */
export type FieldErrors = Record<string, string>;

/** Form-level alert + optional per-field messages. */
export type FormErrors = {
  form: string | null;
  fields: FieldErrors;
};

export function emptyFormErrors(): FormErrors {
  return { form: null, fields: {} };
}

export function hasFieldErrors(fields: FieldErrors): boolean {
  return Object.keys(fields).length > 0;
}

/** Exact / regex matches for known English API messages → i18n keys. */
const KNOWN_MESSAGES: { test: RegExp; key: keyof TranslationStrings }[] = [
  { test: /^invalid credentials\.?$/i, key: "authInvalidCredentials" },
  { test: /email has already been taken/i, key: "authEmailTaken" },
  { test: /too many attempts/i, key: "authTooManyAttempts" },
  { test: /^the email field is required\.?$/i, key: "authEmailRequired" },
  { test: /^the password field is required\.?$/i, key: "authPasswordRequired" },
  { test: /^the email field must be a valid email address\.?$/i, key: "authEmailInvalid" },
  { test: /^must be a valid email/i, key: "authEmailInvalid" },
  { test: /password.*at least 8/i, key: "authPasswordTooShort" },
  { test: /^the name field is required\.?$/i, key: "cpNameRequired" },
];

const FIELD_LABEL_KEYS: Record<string, keyof TranslationStrings> = {
  email: "fieldLabelEmail",
  password: "fieldLabelPassword",
  name: "fieldLabelName",
  description: "fieldLabelDescription",
  notes: "fieldLabelNotes",
  title: "fieldLabelTitle",
  firstname: "fieldLabelFirstName",
  "first name": "fieldLabelFirstName",
  lastname: "fieldLabelLastName",
  "last name": "fieldLabelLastName",
};

function fieldLabel(raw: string, t: TranslationStrings): string {
  const key = FIELD_LABEL_KEYS[raw.toLowerCase()];
  if (key) {
    const label = t[key];
    if (typeof label === "string") return label;
  }
  // "project name" → "Project name"
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function tpl(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ""));
}

/** Localize a single English API / Laravel validation message when possible. */
export function localizeApiMessage(message: string, t: TranslationStrings): string {
  const trimmed = message.trim();
  if (!trimmed) return t.authErrorFallback;

  for (const { test, key } of KNOWN_MESSAGES) {
    if (test.test(trimmed)) {
      const value = t[key];
      if (typeof value === "string") return value;
    }
  }

  let m: RegExpMatchArray | null;

  m = trimmed.match(/^The (.+?) field is required\.?$/i);
  if (m) {
    return tpl(t.validationFieldRequired, { field: fieldLabel(m[1], t) });
  }

  m = trimmed.match(/^The (.+?) has already been taken\.?$/i);
  if (m) {
    return tpl(t.validationFieldTaken, { field: fieldLabel(m[1], t) });
  }

  m = trimmed.match(/^The (.+?) field must be a valid email address\.?$/i);
  if (m) {
    return tpl(t.validationFieldEmail, { field: fieldLabel(m[1], t) });
  }

  m = trimmed.match(/^The (.+?) field must be at least (\d+) characters\.?$/i);
  if (m) {
    return tpl(t.validationFieldMin, { field: fieldLabel(m[1], t), min: m[2] });
  }

  m = trimmed.match(
    /^The (.+?) field must not be greater than (\d+) characters\.?$/i,
  );
  if (m) {
    return tpl(t.validationFieldMax, { field: fieldLabel(m[1], t), max: m[2] });
  }

  return trimmed;
}

function localizeFields(
  fields: Record<string, string[]> | undefined,
  t: TranslationStrings,
  fieldMap?: Record<string, string>,
): FieldErrors {
  if (!fields) return {};
  const out: FieldErrors = {};
  for (const [apiKey, messages] of Object.entries(fields)) {
    const first = messages?.[0];
    if (!first) continue;
    const formKey = fieldMap?.[apiKey] ?? apiKey;
    out[formKey] = localizeApiMessage(first, t);
  }
  return out;
}

export type ResolveServerErrorOptions = {
  /** Fallback when the error is not an ApiError / has no message. */
  fallback?: string;
  /** Remap API detail keys → form field keys (e.g. `{ title: "name" }`). */
  fieldMap?: Record<string, string>;
};

/**
 * Turn a thrown API / unknown error into a top-of-form alert plus optional
 * per-field messages (from `error.details` / `error.fields`).
 */
export function resolveServerError(
  err: unknown,
  t: TranslationStrings,
  options: ResolveServerErrorOptions = {},
): FormErrors {
  const fallback = options.fallback ?? t.authErrorFallback;

  if (isTooManyRequests(err)) {
    return { form: t.authTooManyAttempts, fields: {} };
  }

  if (isApiError(err)) {
    return resolveApiError(err, t, fallback, options.fieldMap);
  }

  if (err instanceof Error && err.message) {
    return { form: localizeApiMessage(err.message, t), fields: {} };
  }

  return { form: fallback, fields: {} };
}

/** True for account-level conflicts — show in the top alert, not on the input. */
function isFormOnlyMessage(message: string, t: TranslationStrings): boolean {
  /* istanbul ignore next -- the exact-match shortcut is already handled before localization */
  if (message === t.authEmailTaken) return true;
  if (/already been taken|already (registered|in use)/i.test(message)) return true;
  // Localized generic "taken" template (e.g. "This Email is already taken.").
  const takenNeedle = t.validationFieldTaken.split("{field}")[0]?.trim();
  if (takenNeedle && message.includes(takenNeedle)) return true;
  return false;
}

/**
 * Lift uniqueness / account conflicts out of `fields` so inputs stay unhighlighted
 * and the copy lives only in the top-of-form alert.
 */
function promoteFormOnlyFieldErrors(fields: FieldErrors, t: TranslationStrings): FormErrors {
  const kept: FieldErrors = {};
  const promoted: string[] = [];
  for (const [key, message] of Object.entries(fields)) {
    if (isFormOnlyMessage(message, t)) promoted.push(message);
    else kept[key] = message;
  }
  if (promoted.length === 0) {
    return { form: null, fields };
  }
  const form =
    promoted.length === 1
      ? promoted[0]
      : Object.keys(kept).length > 0
        ? t.formCheckFields
        : promoted[0];
  return { form, fields: kept };
}

function resolveApiError(
  err: ApiError,
  t: TranslationStrings,
  fallback: string,
  fieldMap?: Record<string, string>,
): FormErrors {
  if (err.code === "INVALID_CREDENTIALS") {
    return { form: t.authInvalidCredentials, fields: {} };
  }

  // Email already registered — top alert only, never paint the email input red.
  if (
    /email has already been taken/i.test(err.message) ||
    Object.values(err.fields ?? {}).some((msgs) =>
      msgs?.some((m) => /email has already been taken/i.test(m)),
    )
  ) {
    return { form: t.authEmailTaken, fields: {} };
  }

  const localizedFields = localizeFields(err.fields, t, fieldMap);
  const promoted = promoteFormOnlyFieldErrors(localizedFields, t);
  const fields = promoted.fields;
  const fieldMessages = Object.values(fields);

  if (err.code === "VALIDATION_ERROR" || fieldMessages.length > 0 || promoted.form) {
    if (promoted.form && fieldMessages.length === 0) {
      return { form: promoted.form, fields: {} };
    }
    // Prefer a single localized field message; otherwise a short prompt.
    const form =
      promoted.form ??
      (fieldMessages.length === 1
        ? fieldMessages[0]
        : fieldMessages.length > 1
          ? t.formCheckFields
          : localizeApiMessage(err.message, t) || fallback);
    return { form, fields };
  }

  const form = localizeApiMessage(err.message, t) || fallback;
  return { form, fields };
}
